'use client'
import React, { useContext, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Column from "@/components/ContentElements/Grid/Column";
import { NewsTeaserProps } from "@/libs/contentful/fetch/content/news";
import NewsTeaser from "@/components/ContentElements/News/NewsTeaser/NewsTeaser";
import {
    ContentContext,
    ContentTypeContext,
    CountryContext,
    YearContext
} from "@/components/ContentElements/News/Provider/NewsFilterProvider";
import Text from "@/components/typographie/Text/Text";
import IconRenderer from "@/libs/contentful/helper/IconRenderer";
import { AssetFields } from "contentful";
import { useTranslations } from "next-intl";
import {
    numberOfContentTagsContext,
    NumberOfContentTypesContext,
    NumberOfCountriesContext,
    NumberOfYearsContext
} from "@/components/ContentElements/News/Provider/NewsCounterProvider";
import ReactPaginate from "react-paginate";
interface Props {
    newsList: NewsTeaserProps[]
    contentTags?: string[] | null
    filterActive?: boolean
    numberOfShownNewsTeaser: number
    isPaginationEnabled: boolean
}

export default function NewsList({ newsList, contentTags, filterActive = false, numberOfShownNewsTeaser, isPaginationEnabled: isPaginationEnabled }: Props) {
    const t = useTranslations();
    const router = useRouter();
    const searchParams = useSearchParams();
    const [sortOrder, setSortOrder] = useState('asc');

    // Get the current page from the URL or default to 1
    const currentPage = parseInt(searchParams.get("page") || "1", 10);

    const { filterContent } = useContext(ContentContext);
    const { filterContentType } = useContext(ContentTypeContext);
    const { filterCountry } = useContext(CountryContext);
    const { filterYear } = useContext(YearContext);

    const { numberOfContentTags } = useContext(numberOfContentTagsContext);
    const { numberOfContentTypes } = useContext(NumberOfContentTypesContext);
    const { numberOfCountries } = useContext(NumberOfCountriesContext);
    const { numberOfYears } = useContext(NumberOfYearsContext);

    const resetCounter = (arr: { [key: string]: number }[]) => {
        arr.forEach(obj => {
            Object.keys(obj).forEach(key => {
                obj[key] = 0;
            });
        });
    };

    resetCounter([numberOfContentTags, numberOfContentTypes, numberOfCountries, numberOfYears]);

    const newsCounter = (obj: { [key: string]: number }, arr: string[]) => {
        arr.forEach(item => obj.hasOwnProperty(item) ? obj[item] += 1 : null);
    };

    newsList.forEach(item => {
        if (!item.contentTags || !item.contentType || !item.date) return;
        newsCounter(numberOfContentTags, item?.contentTags);
        newsCounter(numberOfContentTypes, [item.contentType]);
        if (item.country) {
            const countries = Array.isArray(item.country) ? item.country : [item.country];
            newsCounter(numberOfCountries, countries);
        }
        const year = new Date(item.date).getFullYear();
        newsCounter(numberOfYears, [String(year)]);
    });

    if (!contentTags) newsList.sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf());
    newsList = sortOrder === 'asc' ? newsList : [...newsList].reverse();

    let filteredNews = newsList;
    if (filterActive) {
        filteredNews = newsList.filter(item => {
            if (!item.contentTags || !item.contentType || !item.date) return false;
            return (
                (filterContent === '' || item.contentTags.some(contentTags => contentTags === filterContent)) &&
                (filterContentType === '' || item.contentType === filterContentType) &&
                (filterCountry === '' || (Array.isArray(item.country) ? item.country.includes(filterCountry) : item.country === filterCountry)) &&
                (filterYear === '' || String(new Date(item.date).getFullYear()) === filterYear)
            );
        });
    }

    const totalPages = Math.ceil(filteredNews.length / numberOfShownNewsTeaser);
    const paginatedNews = filteredNews.slice(
        (currentPage - 1) * numberOfShownNewsTeaser,
        currentPage * numberOfShownNewsTeaser
    );

    const changeOrder = () => {
        if (!document.startViewTransition) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
            return;
        }
        document.startViewTransition(() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'));
    };

    const handlePageChange = (selectedItem: { selected: number }) => {
        router.push(`?page=${selectedItem.selected+1}`);
    };

    useEffect(() => {
        if (currentPage > Math.ceil(filteredNews.length / numberOfShownNewsTeaser)) {
            router.push("?page=1");
        }
        // eslint-disable-next-line
    }, [filterContent, filterContentType, filterCountry, filterYear, filteredNews.length]);

    return (
        <>
            {filterActive && <div className="flex items-center justify-between w-full">
                <Text>{filteredNews.length} results</Text>
                <button type="button" onClick={changeOrder}
                        className="text-p font-sans md:whitespace-pre-wrap font-bold flex items-center gap-xs">
                    <IconRenderer icon="icon-sort" className="" />
                    {sortOrder === 'asc' ? t("News.newToOld") : t("News.oldToNew")}
                    <IconRenderer icon="icon-chevron-down" className='' />
                </button>
            </div>}
            {filteredNews.length > 0 && paginatedNews.map((news) => (
                <Column key={news.id} width="1/3">
                    <NewsTeaser slug={news.slug}
                                media={news.image as AssetFields}
                                headline={news.headline}
                                contentTags={news.contentTags}
                                contentType={news.contentType}
                                contentTypeIcon={news.contentTypeIcon}
                                date={news.date}
                                country={news.country}
                    />
                </Column>
            ))}
            {isPaginationEnabled && totalPages > 1 && (
                <ReactPaginate 
                    pageCount={totalPages}
                    onPageChange={handlePageChange}
                    nextLabel={false}
                    previousLabel={false}
                    nextClassName="hidden"
                    previousClassName="hidden"
                    containerClassName="flex items-center justify-center w-full list-[none]"
                    activeClassName="!bg-black text-white border-black"
                    pageClassName="mx-s w-xl md:w-3xl py-s inline-flex justify-center rounded border bg-white text-black hover:bg-gray" //mx-s py-m sm:mx-xs
                    pageLinkClassName="m-auto"
                    pageRangeDisplayed={2}
                    marginPagesDisplayed={1}
                    breakLabel={<span>...</span>}
                    initialPage={currentPage-1}
                />
            )}
        </>
    );
}